"use client"

import { useChains } from "@opensea/app-kit"
import type { BlockchainAction } from "@opensea/graphql"
import { useToasts } from "@opensea/ui-kit"
import type { Awaitable } from "@opensea/utils"
import { getPropertyOrUndefined } from "@opensea/utils"
import { isEqual } from "es-toolkit/compat"
import type { FragmentOf, ResultOf } from "gql.tada"
import { readFragment } from "gql.tada"
import { useCallback, useEffect, useRef, useState } from "react"
import type { UseQueryExecute } from "urql"
import { useTranslations } from "use-intl"
import {
  encodeAbiParameters,
  encodeFunctionData,
  type TypedData,
  type TypedDataParameter,
} from "viem"
import { useBlurAuth } from "../../blur/"
import {
  AWAITED_TRANSACTION_TYPES,
  RECEIPT_TRANSACTION_TYPES,
} from "../../flow/BottomSheetFlows/constants"
import { useAddReceiptTransaction } from "../../flow/hooks/useAddReceiptTransactions"
import { useAllActionsCompleted } from "../../flow/hooks/useAllActionsCompleted"
import { useErrors } from "../../flow/hooks/useErrors"
import { useIsAbstractWalletAccount } from "../../hooks/useIsAbstractWalletAccount"
import { useTrackError } from "../../providers/TrackErrorProvider"
import type { TransactionIdentifier } from "../../stores/currentTransactionStore"
import { CurrentTransactionActions } from "../../stores/currentTransactionStore"
import type { PolychainError } from "../lib/errors"
import {
  IncompatibleActionError,
  InsufficientFundsError,
  RequestExpiredError,
  TransactionTimedOutError,
  WalletRejectedError,
} from "../lib/errors"
import type {
  Signature,
  TransactFunction,
  TransactFunctionOptions,
  WaitFunction,
} from "../types"
import { useAccount } from "./useAccount"
import { isMetaMaskConnector, useConnectors } from "./useConnectors"
import { useIsEmbeddedWallet } from "./useIsEmbeddedWallet"
import {
  useScheduler_actionFragment,
  useScheduler_readShouldBufferGasFragment,
  useScheduler_signatureRequestFragment,
  useScheduler_svmTransactionSubmissionDataFragment,
  useScheduler_transactionSubmissionDataFragment,
} from "./useScheduler.fragment"
import { useSignature } from "./useSignature"
import { useSvmTransact } from "./useSvmTransact"
import { useTransact } from "./useTransact"

// Permit2 and Universal Router constants for Permit2 signature actions
const UNIVERSAL_ROUTER_ADDRESS = "0x66a9893cc07d91d95644aedd05d03f95e1dba8af"

const UNIVERSAL_ROUTER_ABI = [
  {
    inputs: [
      { internalType: "bytes", name: "commands", type: "bytes" },
      { internalType: "bytes[]", name: "inputs", type: "bytes[]" },
      { internalType: "uint256", name: "deadline", type: "uint256" },
    ],
    name: "execute",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
] as const

export type SchedulableSignatureResponse = {
  action: "SIGNATURE"
  signature: Signature
}

export type SchedulableTransactionResponse = {
  action: "TRANSACTION"
  type: BlockchainAction["__typename"]
  transactionIdentifier: TransactionIdentifier
  wait: WaitFunction
}

export type SchedulableResponse =
  | SchedulableSignatureResponse
  | SchedulableTransactionResponse

type UseSchedulerOpts = {
  actions: FragmentOf<typeof useScheduler_actionFragment>[]

  /**
   * Callback fired when all actions have been sent
   */
  onAllActionsCompleted?: (
    lastResponse: SchedulableResponse,
    receiptTransactions: TransactionIdentifier[],
  ) => Awaitable<void>
  /**
   * Callback fired when the wallet is refreshed
   */
  onReload?: UseQueryExecute
  /**
   * Callback fired when the current action changes
   */
  onActionIndexChange?: (newActionIndex: number) => void
  /**
   * Callback fired when an action has been sent
   */
  onActionCompleted?: (
    lastResponse: SchedulableResponse,
    index: number,
  ) => Awaitable<void>
  /**
   * Whether to automatically start the scheduler when an embedded wallet is connected.
   * Confirmation and starting of the action is usually handled by ActionTimeline for embedded wallets.
   */
  autoStartEmbeddedWallet?: boolean
}

/**
 * Hook that will run an array of BlockchainActions in sequence.
 * @param transactions - array of transactions or signatures to be sent
 * @param opts - optional parameters
 * @returns object:
 *  - start: function that starts the transaction sequence. This function can only be called once.
 *  - resume: function that resumes the transaction sequence from the current index
 *  - current: current index in the transaction sequence
 *  - error: error message if the transaction sequence fails
 *  - isSending: boolean indicating if the transaction sequence is currently sending
 */
export function useScheduler({
  actions: actionKeys,
  onReload,
  onAllActionsCompleted,
  onActionIndexChange,
  onActionCompleted,
  autoStartEmbeddedWallet = false,
}: UseSchedulerOpts) {
  const actions = actionKeys.map(a =>
    readFragment(useScheduler_actionFragment, a),
  )

  const [current, setCurrent] = useState(0)
  const [isDone, setIsDone] = useState(false)
  const [error, setError] = useState<string>()
  const { setActionError } = useErrors()

  const actionsRef = useRef<typeof actions>([])

  /** The current action is pending when we are waiting on wallet input */
  const [isPending, setIsPending] = useState(false)

  /** The current action is loading when are waiting for an onchain transaction to finalize, or the server to respond in some capacity */
  const [isLoading, setIsLoading] = useState(false)

  const account = useAccount()
  const { getConnectorStrict } = useConnectors()
  const { getChainIdentifier, allChains } = useChains()

  const { sign, signTyped } = useSignature()
  const { transact } = useTransact()
  const { blurAuth } = useBlurAuth()

  const trackError = useTrackError()

  const addReceiptTransaction = useAddReceiptTransaction()

  const validateAction = useValidateAction()

  const handleActionCompleted = useCallback(
    async (response: SchedulableResponse, index: number) => {
      if (response.action !== "TRANSACTION") {
        return
      }
      if (AWAITED_TRANSACTION_TYPES.includes(response.type)) {
        await response.wait()
      }
      if (RECEIPT_TRANSACTION_TYPES.includes(response.type)) {
        addReceiptTransaction(response.transactionIdentifier)
      }
      await onActionCompleted?.(response, index)
    },
    [addReceiptTransaction, onActionCompleted],
  )

  const [, setAllActionsCompleted] = useAllActionsCompleted()

  const isEmbeddedWallet = useIsEmbeddedWallet()

  /**
   * Check if actions require explicit user confirmation (not auto-startable).
   * Buy and accept offer actions should always require the user to click the action button.
   */
  const requiresExplicitConfirmation = actions.some(
    action =>
      action.__typename === "BuyItemAction" ||
      action.__typename === "SvmBuyItemsAction" ||
      action.__typename === "AcceptOfferAction",
  )

  /**
   * Signature function that understands SignatureRequest types
   * @param signatureRequest - the signature request object
   * @returns the signature
   * @throws if the signature request type is unknown
   */
  const signForAction: (
    signatureRequestKey: FragmentOf<
      typeof useScheduler_signatureRequestFragment
    >,
  ) => Promise<string> = useCallback(
    async signatureRequestKey => {
      if (!account.address) {
        throw new Error("No account connected while attempting wallet actions")
      }

      const signatureRequest = readFragment(
        useScheduler_signatureRequestFragment,
        signatureRequestKey,
      )
      switch (signatureRequest.__typename) {
        case "PersonalSignRequest":
          return sign({
            address: account.address,
            message: signatureRequest.message,
          })
        case "SignTypedDataRequest":
          return signTyped({
            ...JSON.parse(signatureRequest.message),
            address: account.address,
          })
        default:
          throw new Error(
            "Unknown signature request type: " +
              JSON.stringify(signatureRequest),
          )
      }
    },
    [account.address, sign, signTyped],
  )

  /**
   * Transaction function that understands BlockchainActions transactionSubmissionData
   * @param transactionSubmissionData - the transaction submission data
   * @returns the transaction
   */
  const transactForAction: (
    transactionSubmissionDataKey: FragmentOf<
      typeof useScheduler_transactionSubmissionDataFragment
    >,
    options?: TransactFunctionOptions,
  ) => ReturnType<TransactFunction> = useCallback(
    (transactionSubmissionDataKey, options) => {
      const transactionSubmissionData = readFragment(
        useScheduler_transactionSubmissionDataFragment,
        transactionSubmissionDataKey,
      )

      const chainId = transactionSubmissionData.chain.networkId
      return transact(
        {
          address: account.address,
          to: transactionSubmissionData.to as `0x${string}`,
          data: transactionSubmissionData.data as `0x${string}`,
          value: BigInt(transactionSubmissionData.value),
          chainId,
        },
        options,
      )
    },
    [account.address, transact],
  )

  const { transact: svmTransact } = useSvmTransact()
  const transactForSvmAction = useCallback(
    (
      transactionSubmissionDataKey: FragmentOf<
        typeof useScheduler_svmTransactionSubmissionDataFragment
      >,
    ) => {
      const { instructions, addressLookupTableAddresses } = readFragment(
        useScheduler_svmTransactionSubmissionDataFragment,
        transactionSubmissionDataKey,
      )
      return svmTransact({
        instructions,
        addressLookupTableAddresses: addressLookupTableAddresses as string[],
      })
    },
    [svmTransact],
  )

  const runAction = useCallback(
    async (
      index: number,
      responses: SchedulableResponse[] = [],
      retryCount = 0,
    ) => {
      setCurrent(index)

      if (index >= actions.length) {
        setIsDone(true)

        const lastResponse = responses.at(-1)
        if (lastResponse) {
          // Collect receipt transactions from responses
          const receiptTransactions = responses
            .filter(
              (r): r is SchedulableTransactionResponse =>
                r.action === "TRANSACTION" &&
                RECEIPT_TRANSACTION_TYPES.includes(r.type),
            )
            .map(r => r.transactionIdentifier)

          onAllActionsCompleted?.(lastResponse, receiptTransactions)
        }
        setAllActionsCompleted()

        return
      }

      setError(undefined)
      CurrentTransactionActions.setTransaction(undefined)

      const action = readFragment(useScheduler_actionFragment, actions[index])
      onActionIndexChange?.(index)

      try {
        if (!account.address) {
          throw new Error(
            "No account connected while attempting wallet actions",
          )
        }

        validateAction(action)

        // We are pending wallet input at this point
        setIsPending(true)

        // On embedded wallet the user has already confirmed the action
        if (isEmbeddedWallet) {
          setIsLoading(true)
        }

        if (action.__typename === "BlurAuthAction") {
          /**
           * BlurAuthAction is a special action that requires a signature to be exchanged for a
           * blur auth token. This token is sent for subsequent BlockchainAction requests.
           */
          const signature = await signForAction(action.signatureRequest)

          setIsLoading(true)

          await blurAuth({
            address: account.address,
            expiresOn: action.expiresOn,
            hmac: action.hmac,
            message: action.signatureRequest.message,
            chain: action.chain.identifier,
            signature,
          })

          responses.push({
            action: "SIGNATURE",
            signature,
          })
        } else if (action.__typename === "Permit2SignatureAction") {
          /**
           * Permit2SignatureAction is a special action that requires signing a permit2 signature
           * and then using that signature to build a transaction to the Universal Router.
           */
          const {
            domain,
            types,
            transactionBuilder,
            primaryType,
            trxSignatureSuffix,
          } = action
          const { encodedInputs, commands, deadline } = transactionBuilder
          const {
            permitMessageForSigning,
            permitInputTypes,
            payPortionInput,
            swapInputs,
          } = encodedInputs

          // Convert GraphQL types to the format expected by signTyped
          const permitSingleFields: readonly TypedDataParameter[] =
            types.permitSingle.map(field => ({
              name: field.name,
              type: field.type,
            }))
          const permitDetailsFields: readonly TypedDataParameter[] =
            types.permitDetails.map(field => ({
              name: field.name,
              type: field.type,
            }))

          const typedDataTypes = {
            [primaryType]: permitSingleFields,
            PermitDetails: permitDetailsFields,
          } as unknown as TypedData

          // Sign the permit2 message
          const signature = await signTyped({
            address: account.address,
            domain: {
              name: domain.name,
              chainId: domain.chainId,
              verifyingContract: domain.verifyingContract,
            },
            types: typedDataTypes,
            primaryType,
            message: {
              details: {
                token: permitMessageForSigning.details.token,
                amount: permitMessageForSigning.details.amount,
                expiration: permitMessageForSigning.details.expiration,
                nonce: permitMessageForSigning.details.nonce,
              },
              spender: permitMessageForSigning.spender,
              sigDeadline: permitMessageForSigning.sigDeadline,
            },
          })

          setIsLoading(true)

          // Encode the permit input with the signature
          // Parse the complex tuple types from the API response
          const abiTypes = permitInputTypes.map(typeStr => {
            if (typeStr.startsWith("tuple(")) {
              // For tuple types, we need to define the components
              if (
                typeStr.includes(
                  "details, address spender, uint256 sigDeadline",
                )
              ) {
                return {
                  type: "tuple",
                  name: "",
                  components: [
                    {
                      type: "tuple",
                      name: "details",
                      components: [
                        { type: "address", name: "token" },
                        { type: "uint160", name: "amount" },
                        { type: "uint48", name: "expiration" },
                        { type: "uint48", name: "nonce" },
                      ],
                    },
                    { type: "address", name: "spender" },
                    { type: "uint256", name: "sigDeadline" },
                  ],
                }
              }
            }
            return { type: typeStr, name: "" }
          })

          const permitInput = encodeAbiParameters(abiTypes, [
            permitMessageForSigning,
            signature,
          ])

          // Build the transaction using pre-encoded components
          const transactionData = encodeFunctionData({
            abi: UNIVERSAL_ROUTER_ABI,
            functionName: "execute",
            args: [
              commands as `0x${string}`,
              [
                permitInput,
                payPortionInput as `0x${string}`,
                swapInputs as `0x${string}`,
              ],
              BigInt(deadline),
            ],
          })

          // Execute the transaction with the built data
          if (account.connectorId && isMetaMaskConnector(account.connectorId)) {
            // MetaMask does not resolve transaction promise until it is confirmed
            // So set loading state immediately
            setIsPending(false)
            setIsLoading(true)
          }

          // Use the transact function directly since we have all the transaction data
          const tx = await transact(
            {
              address: account.address,
              to: UNIVERSAL_ROUTER_ADDRESS as `0x${string}`,
              data: transactionData + trxSignatureSuffix,
              value: BigInt(0),
              chainId: domain.chainId,
            },
            {
              shouldBufferGas: false, // Don't buffer gas for permit2 transactions
            },
          )

          // We're no longer waiting for user input, our tx is in flight
          setIsPending(false)
          setIsLoading(true)

          // Get chain information using the chain infrastructure
          const chainIdentifier = getChainIdentifier(domain.chainId, "EVM")
          const chain = allChains.find(
            c => c.networkId === domain.chainId && c.arch === "EVM",
          )

          if (!(chainIdentifier && chain)) {
            throw new Error(
              `Unsupported chain ID: ${domain.chainId} for Permit2SignatureAction`,
            )
          }

          CurrentTransactionActions.setTransaction({
            hash: tx.hash,
            chain: chainIdentifier,
          })

          const response: SchedulableTransactionResponse = {
            action: "TRANSACTION",
            type: action.__typename,
            transactionIdentifier: {
              hash: tx.hash,
              chain: chainIdentifier,
            },
            wait: tx.wait,
          }

          await handleActionCompleted(response, index)

          responses.push(response)
        } else if (action.__typename === "RefreshAction") {
          /**
           * RefreshAction is a special action that refreshes the action list.
           * The backend drives this action with a specific message.
           */
          if (!onReload) {
            throw new Error(
              "Encountered RefreshAction but no refresh function provided",
            )
          }

          setIsLoading(true)

          // Refresh the action list and reset the scheduler back to start
          await onReload({
            requestPolicy: "network-only",
          })
          return
        } else {
          /**
           * General BlockchainAction handler
           * If it's not a special case BlockchainAction, we just check for signature and transact fields
           * and call appropriate function and add to responses.
           */

          if ("signatureRequest" in action) {
            const signature = await signForAction(action.signatureRequest)

            responses.push({
              action: "SIGNATURE",
              signature,
            })
          }

          if ("transactionSubmissionData" in action) {
            if (
              account.connectorId &&
              isMetaMaskConnector(account.connectorId)
            ) {
              // MetaMask does not resolve transaction promise until it is confirmed
              // So set loading state immediately
              setIsPending(false)
              setIsLoading(true)
            }

            const tx = await transactForAction(
              action.transactionSubmissionData,
              {
                shouldBufferGas: readShouldBufferGas(action),
              },
            )

            // We're no longer waiting for user input, our tx is in flight
            setIsPending(false)
            setIsLoading(true)

            CurrentTransactionActions.setTransaction({
              hash: tx.hash,
              chain: action.transactionSubmissionData.chain.identifier,
            })

            const response: SchedulableTransactionResponse = {
              action: "TRANSACTION",
              type: action.__typename,
              transactionIdentifier: {
                hash: tx.hash,
                chain: action.transactionSubmissionData.chain.identifier,
              },
              wait: tx.wait,
            }

            await handleActionCompleted(response, index)

            responses.push(response)
          }

          if ("svmTransactionSubmissionData" in action) {
            const tx = await transactForSvmAction(
              action.svmTransactionSubmissionData,
            )

            // We're no longer waiting for user input, our tx is in flight
            setIsPending(false)
            setIsLoading(true)

            const response: SchedulableTransactionResponse = {
              action: "TRANSACTION",
              type: action.__typename,
              transactionIdentifier: {
                hash: tx.hash,
                chain: "solana",
              },
              wait: tx.wait,
            }

            await handleActionCompleted(response, index)

            responses.push(response)
          }
        }

        return await runAction(index + 1, responses)
      } catch (err) {
        const error = err as PolychainError
        if (isRetryableError(error) && retryCount < MAX_RETRIES) {
          // Add a short delay between retry attempts
          await new Promise(resolve => setTimeout(resolve, 500))
          return await runAction(index, responses, retryCount + 1)
        }

        if (isTrackedError(error)) {
          if (!account.connectorId) {
            throw new Error("No connector ID while attempting wallet actions")
          }
          const connector = await getConnectorStrict(account.connectorId)
          const provider =
            connector.chainArch === "EVM"
              ? ((await connector.baseConnector
                  .getProvider()
                  .catch(_ => undefined)) as
                  | Record<string, unknown>
                  | undefined)
              : {}

          trackError(error, {
            extra: {
              action,
              type: "blockchain-action-error",
              retryCount,
              skipSentry: shouldSkipSentry(error),
              providerState: {
                chainId: getPropertyOrUndefined(provider, "chainId"),
                networkVersion: getPropertyOrUndefined(
                  provider,
                  "networkVersion",
                ),
                selectedAddress: getPropertyOrUndefined(
                  provider,
                  "selectedAddress",
                ),
              },
            },
            tags: { action: action.__typename, name: error.name },
          })
        } else {
          // Log untracked errors to console
          console.error(error)
        }

        setError(error.message)
        setActionError(error.message)
      } finally {
        setIsLoading(false)
        setIsPending(false)
      }
    },
    [
      actions,
      onActionIndexChange,
      onAllActionsCompleted,
      setAllActionsCompleted,
      account.address,
      account.connectorId,
      validateAction,
      isEmbeddedWallet,
      signForAction,
      blurAuth,
      onReload,
      transactForAction,
      handleActionCompleted,
      transactForSvmAction,
      setActionError,
      getConnectorStrict,
      trackError,
    ],
  )

  /**
   * Anytime actions changes, we want to start the scheduler.
   * Most often this happens whenever actions get initially populated.
   * For RefreshAction we will call onReload and reset the scheduler.
   */
  useEffect(() => {
    if (actions.length === 0) {
      actionsRef.current = []
      return
    }

    if (isEqual(actions, actionsRef.current)) {
      return
    }

    // Don't auto-start if explicit confirmation is required (e.g., buy actions)
    // On embedded wallet we need user confirmation
    if (
      !requiresExplicitConfirmation &&
      (!isEmbeddedWallet || autoStartEmbeddedWallet)
    ) {
      runAction(0)
    }
    actionsRef.current = actions
  }, [
    actions,
    actionsRef,
    isEmbeddedWallet,
    runAction,
    autoStartEmbeddedWallet,
    requiresExplicitConfirmation,
  ])

  const retry = useCallback(() => {
    if (actions.length === 0) {
      return
    }
    if (current >= actions.length) {
      return
    }
    runAction(current)
  }, [current, actions, runAction])

  return {
    start: retry,
    retry,
    current,
    error,
    isPending,
    isLoading,
    isDone,
  }
}

function isTrackedError(error: Error) {
  if (error instanceof InsufficientFundsError) {
    return false
  }
  return true
}

const MAX_RETRIES = 2

function isRetryableError(error: Error) {
  if (error instanceof WalletRejectedError) {
    return false
  }
  if (error instanceof InsufficientFundsError) {
    return false
  }
  if (error instanceof IncompatibleActionError) {
    return false
  }
  return true
}

// these errors represent normal control flow, and should not be sent to sentry
function shouldSkipSentry(error: Error): boolean {
  if (error instanceof WalletRejectedError) {
    return true
  }
  if (error instanceof InsufficientFundsError) {
    return true
  }
  if (error instanceof RequestExpiredError) {
    return true
  }
  if (error instanceof TransactionTimedOutError) {
    return true
  }
  return false
}

function readShouldBufferGas(
  actionKey: FragmentOf<typeof useScheduler_readShouldBufferGasFragment>,
) {
  const action = readFragment(
    useScheduler_readShouldBufferGasFragment,
    actionKey,
  )

  // Buffer same chain swaps
  if (
    action.__typename === "SwapAssetsAction" &&
    action.relayerFulfillment?.sameChain
  ) {
    return true
  }

  // Buffer polygon and berachain swaps and buys
  if (
    (action.__typename === "SwapAssetsAction" ||
      action.__typename === "BuyItemAction") &&
    (action.transactionSubmissionData.chain.identifier === "polygon" ||
      action.transactionSubmissionData.chain.identifier === "bera_chain")
  ) {
    return true
  }

  // TODO: temporary fix for rabby wallet gas estimation
  return (
    "transactionSubmissionData" in action &&
    action.transactionSubmissionData?.chain?.identifier === "somnia"
  )
}

/**
 * General guard for when we do not want to execute an action
 */
function useValidateAction() {
  const t = useTranslations("wallet.useScheduler")
  const isAbstractWallet = useIsAbstractWalletAccount()
  const { showErrorMessage } = useToasts()

  return useCallback(
    (actionKey: ResultOf<typeof useScheduler_actionFragment>) => {
      const action = readFragment(useScheduler_actionFragment, actionKey)

      if (
        "relayerFulfillment" in action &&
        action.relayerFulfillment?.crossChain
      ) {
        if (isAbstractWallet) {
          showErrorMessage(t("abstractCrossChainNotAllowed"))
          throw new IncompatibleActionError(
            new Error(t("abstractCrossChainNotAllowed")),
          )
        }
      }

      return action
    },
    [isAbstractWallet, showErrorMessage, t],
  )
}
